import { vec3 } from "gl-matrix";
import { Subject } from "rxjs";
import { Ray } from "@spi/Common/Math";
import { PathNode, NavigationMeshPathFinder, PathSegment } from "@spi/Common/NavigationMesh";
import { SpatialDatabase } from "@spi/Common/Util";
import { TimedMovement } from "./TimedMovement";

export class NavigationAwareObject {

    private behaviours: Set<INavigationBehaviour>;
    public movement: TimedMovement<MovementNode>;
    public pathChanged: Subject<PathSegment[]>;

    constructor(public navigationObject: NavigationObject, public navigationData: NavigationData, speed: number) {
        this.behaviours = new Set<INavigationBehaviour>();
        this.movement = new TimedMovement<MovementNode>(this.navigationObject, {
            position: this.navigationObject.position,
            node: this.getPathNode(this.navigationObject.position, 10)
        }, speed);

        this.pathChanged = new Subject<PathSegment[]>();

        this.movement.onStep.subscribe(mv => {
            this.navigationObject.position[2] = this.movement.currentNode.node.getHeight(this.navigationObject.position);
        });
    }

    private getPathNode(position: vec3 | number[], heightRange: number): PathNode {
        // Find the node for the position
        let posNear = vec3.clone(position);
        let posFar = vec3.clone(position);
        posNear[2] = position[2] + heightRange;
        posFar[2] = position[2] - heightRange;

        let ray = new Ray(posNear, posFar);
        let rayResults = this.navigationData.navigationDatabase.rayTrace(ray);

        // TODO: what if object is out of bounds?
        if (rayResults.length > 0) {
            return rayResults[0].object as PathNode;
        }
        else {
            return null;
        }
    }

    public bind(behaviour: INavigationBehaviour) {
        behaviour.attach(this);
        this.behaviours.add(behaviour);
    }

    public unbind(behaviour: INavigationBehaviour) {
        behaviour.detach(this);
        this.behaviours.delete(behaviour);
    }

    public navigateToPosition(position: vec3 | number[]) {
        let node = this.getPathNode(position, 25);
        if (!node) {
            // Nothing to navigate to
            return;
        }

        let route = this.navigationData.pathFinder.findPath(this.movement.currentNode.node, node, this.navigationObject.position, position);

        if (route.solved) {
            let path = this.navigationData.pathFinder.optimizeMovementPath(this.movement.currentNode.node, route);
            this.pathChanged.next(path);
            this.movement.setPath(path);
        }
    }

    public navigateToNode(targetNode: PathNode, position: vec3 | number[]) {
        let route = this.navigationData.pathFinder.findPath(this.movement.currentNode.node, targetNode, this.navigationObject.position, position);

        if (route.solved) {
            let path = this.navigationData.pathFinder.optimizeMovementPath(this.movement.currentNode.node, route);
            this.pathChanged.next(path);
            this.movement.setPath(path);
        } else {
            console.warn('no path', this.movement.currentNode.node, targetNode);
        }
    }
}

export interface INavigationBehaviour {
    attach(navObject: NavigationAwareObject): void;
    detach(navObject: NavigationAwareObject): void;
}

type NavigationObject = {
    position: vec3 | number[]
}

type MovementNode = {
    position: vec3 | number[],
    node: PathNode
}

type NavigationData = {
    pathFinder: NavigationMeshPathFinder,
    navigationDatabase: SpatialDatabase<PathNode>
}